import React from "react";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { cn } from "@/lib/utils";

// Consistent colour palette — same hash logic as ChatDiscussion
const AVATAR_COLOURS = [
  "bg-blue-500",
  "bg-purple-500",
  "bg-green-500",
  "bg-orange-500",
  "bg-pink-500",
  "bg-indigo-500",
  "bg-teal-500",
  "bg-red-500",
  "bg-amber-500",
  "bg-cyan-500",
  "bg-rose-500",
  "bg-violet-500",
];

/**
 * Get initials from a full name.
 * "Sarah Mitchell" → "SM", "David Chen" → "DC"
 */
export function getInitials(name: string): string {
  const parts = name.trim().split(/\s+/);
  if (parts.length >= 2) {
    return `${parts[0][0]}${parts[parts.length - 1][0]}`.toUpperCase();
  }
  return name.substring(0, 2).toUpperCase();
}

/**
 * Deterministic colour based on user ID or name — same user always gets
 * the same colour, across every screen they appear on.
 */
export function getUserAvatarColour(seed: string): string {
  let hash = 0;
  for (let i = 0; i < seed.length; i++) {
    hash = seed.charCodeAt(i) + ((hash << 5) - hash);
  }
  return AVATAR_COLOURS[Math.abs(hash) % AVATAR_COLOURS.length];
}

interface UserAvatarProps {
  /** Full name — used for initials fallback */
  name: string;
  /** Seed for colour derivation — use user ID if available, otherwise name */
  seed?: string;
  /** Optional uploaded avatar image URL */
  avatarUrl?: string | null;
  /** Size variant */
  size?: "xs" | "sm" | "md" | "lg";
  /** Additional class names */
  className?: string;
}

const SIZE_CLASSES: Record<NonNullable<UserAvatarProps["size"]>, string> = {
  xs: "h-6 w-6 text-[10px]",
  sm: "h-8 w-8 text-xs",
  md: "h-10 w-10 text-sm",
  lg: "h-14 w-14 text-lg",
};

/**
 * UserAvatar — shared component used across the entire application.
 *
 * Renders:
 *  1. Uploaded photo (if avatarUrl provided)
 *  2. Coloured circle with initials (deterministic — same user always same colour)
 *
 * Usage:
 *   <UserAvatar name="Sarah Mitchell" seed="user-6" size="sm" />
 *   <UserAvatar name="David Chen" seed="user-2" avatarUrl={prefs.avatarUrl} size="md" />
 */
export function UserAvatar({
  name,
  seed,
  avatarUrl,
  size = "sm",
  className,
}: UserAvatarProps) {
  // Always use name as the seed — consistent across every screen regardless
  // of what identifier is available (persona ID, user.id, etc.)
  const colourSeed = name;
  const colour = getUserAvatarColour(colourSeed);
  const initials = getInitials(name);

  return (
    <Avatar className={cn(SIZE_CLASSES[size], className)}>
      {avatarUrl && (
        <AvatarImage src={avatarUrl} alt={name} className="object-cover" />
      )}
      <AvatarFallback className={cn(colour, "text-white font-semibold")}>
        {initials}
      </AvatarFallback>
    </Avatar>
  );
}
